<template>
    <div
        :class="{
            'has-label': !!label && !isNewDesign,
            'custom-select-new': isNewDesign,
            'custom-select-new--error': errorMessage,
            'custom-select-new--disabled': $attrs.disabled,
            'custom-select-new--input-not-empty': this.isNotEmpty,
        }"
        class="form__field custom-select"
    >
        <label
            v-if="label"
            class="input__label"
        >{{ $capitalize($t(label).toString()) }}</label>
        <select
            :value="value"
            @input="onInput"
            @blur="onBlur"
            :class="{ 'is-invalid': errorMessage, 'is-placeholder': isPlaceholderValue }"
            class="select__field form-control form-control-sm"
            v-bind="$attrs"
        >
            <option
                v-if="isNewDesign"
                hidden="true"
                value=""
            ></option>
            <option
                v-else
                :hidden="!showEmptyOption"
                value=""
            >
                {{ $capitalize($t(placeholder || label).toString()) }}{{ labelSuffix }}
            </option>
            <option
                v-for="option in options"
                :value="option[idField]"
                :key="option[idField]"
                :selected="selected && selected[idField] === option[idField]"
                :disabled="option.isDisabled"
            >
                {{ displayValue(option) }}
            </option>
        </select>
        <Icon
            v-if="isNewDesign && errorMessage"
            size="sm"
            class="icon-warning_triangle_filled select__field-icon-error"
        />
        <Icon
            class="icon-chevron_down select__field-chevron"
        />
        <div
            v-if="errorMessage"
            class="message message--error"
        >
            <Icon
                v-if="!isNewDesign"
                size="xs"
                class="icon-warning_triangle"
            />

            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';

    export default Vue.component('CustomSelect', {
        emits: [
            'input',
            'blur',
            'valueChanged',
            'filterInput',
        ],
        props: {
            value: {
                type: [Number, String],
                required: false,
                default: null,
            },
            name: {
                type: String,
                required: true,
            },
            label: {
                type: String,
                required: false,
                default: '',
            },
            labelSuffix: {
                type: String,
                required: false,
                default: '',
            },
            options: {
                type: Array,
                required: true,
            },
            showEmptyOption: {
                type: Boolean,
                required: false,
                default: true,
            },
            displayField: {
                type: String,
                required: false,
                default: 'label',
            },
            displayFunction: {
                type: Function,
                required: false,
                default: undefined,
            },
            selected: {
                type: Object,
                required: false,
                default: null,
            },
            isRequired: {
                type: Boolean,
                required: false,
                default: false,
            },
            errorMessage: {
                type: String,
                required: false,
                default: null,
            },
            idField: {
                type: String,
                required: false,
                default: 'id',
            },
            placeholder: {
                type: String,
                required: false,
                default: '',
            },
            isNewDesign: {
                type: Boolean,
                required: false,
                default: false,
            },
            clearValueOnOptionsChange: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data() {
            return {
                isDirty: false,
                error: '',
                filterInput: '',
            };
        },
        computed: {
            isPlaceholderValue() {
                const matchingOption = this.options.find(({ [this.idField]: id }) => id === this.value);
                return matchingOption ? matchingOption.isDisabled : false;
            },
            isNotEmpty() {
                return this.value !== null && this.value !== undefined && this.value !== '';
            },
        },
        watch: {
            options: {
                handler() {
                    if (this.clearValueOnOptionsChange) {
                        this.$emit('input', null);
                        this.$emit('valueChanged', null, this.name);
                    }
                },
                deep: true,
            },
        },
        methods: {
            onInput(e) {
                this.isDirty = true;
                this.$emit('input', e.target.value);
                this.$emit('valueChanged', e.target.value, this.name);
            },
            onBlur() {
                this.$emit('blur', this.name, this.value, this.isDirty);
            },
            onFilterInput(value) {
                this.filterInput = value;
                this.$emit('filterInput', this.filteredOptions);
            },
            displayValue(option) {
                if (this.displayFunction) {
                    return this.displayFunction(option);
                }
                return option[this.displayField];
            },
        },
    });
</script>

<style lang="scss" scoped>
    @import '@superbet-group/web.lib.global-styles/colors.scss';
    @import '~@superbet-group/web.lib.global-styles/type.scss';

    $sb-input__field__background-color: $neutral-98 !default;
    $sb-input__field__text-color: $neutral-24 !default;
    $sb-input__wrapper--focus-within__border-color: $neutral-52 !default;
    $sb-input__wrapper--error__background-color: $chrom-yellow-100-dd !default;
    $sb-input__wrapper--error__border-color: $chrom-orange-95 !default;
    $sb-input__message__text-color: $neutral-13 !default;
    $sb-input__icon--error__text-color: $chrom-orange-95 !default;
    $sb-input__label__text-color: $neutral-24 !default;
    $sb-input__label--focus-within__text-color: $neutral-63 !default;

    .custom-select-new {
        .input__label {
            position: absolute;
            display: block;
            top: 50%;
            left: 12px;
            transform: translateY(-50%);
            @include font-14-regular;
            line-height: 17px;
            color: $sb-input__label__text-color;
            transition: all 250ms;
        }
        &:focus-within,
        &.custom-select-new--input-not-empty {
            .input__label {
                top: 7px;
                transform: translateY(0);
                @include font-10-regular;
                line-height: 12px;
                color: $sb-input__label--focus-within__text-color;
            }
        }
        &--disabled {
            opacity: 0.5;
            .select__field {
                opacity: 1;
            }
        }
        .select__field {
            height: 50px;
            border-radius: 5px;
            border-width: 1px;
            background-color: $sb-input__field__background-color;
            color: $sb-input__field__text-color;
            padding: 12px 3px 0 12px;
            @include font-16-medium;

        }

        ::v-deep.select__field-chevron {
            top: 50% !important;
            right: 0px;
        }

        &--error {
            .select__field {
                border-color: $sb-input__wrapper--error__border-color;
                background-color: $sb-input__wrapper--error__background-color;
                border-width: 2px;
            }
            ::v-deep.select__field-icon-error {
                position: absolute;
                right: 20px;
                top: 22%;
                color: $sb-input__icon--error__text-color;
                font-size: 18px;
                width: 18px;
                height: 18px;
            }
            ::v-deep.select__field-chevron {
                top: 35% !important;
            }
            ::v-deep.message--error {
                @include font-11-regular;
                line-height: 13px;
                margin: 5px 6px 0 6px;
                color: $sb-input__message__text-color;
            }
            .input__label {
                top: 35%;
            }
        }
    }
</style>
